import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp,
  TrendingDown,
  LocalShipping,
  Speed,
  Storage,
  Refresh,
  Download,
  ContentCopy,
  Search,
  Clear,
  Upload,
  ExpandMore,
  Check,
  ArrowUpward,
  ArrowDownward,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useApiClient } from '../utils/apiClient';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const DEFAULT_EVENT_LIMIT = 500;

const buildSummaryFallback = (events = [], endpointBreakdown = {}) => {
  const totalEvents = events.length;
  const successfulEvents = events.filter(event => event.success);
  const successRate = totalEvents > 0 ? (successfulEvents.length / totalEvents) * 100 : 0;
  const responseTimes = events
    .filter(event => typeof event.totalRequestTime === 'number' && event.totalRequestTime >= 0)
    .map(event => event.totalRequestTime);
  const averageResponseTime = responseTimes.length > 0
    ? responseTimes.reduce((acc, time) => acc + time, 0) / responseTimes.length
    : 0;

  const filesGenerated = {
    zpl: events.filter(event => event.labelProcessingMetrics?.filesGenerated?.zpl).length,
    pdf: events.filter(event => event.labelProcessingMetrics?.filesGenerated?.pdf).length,
    png: events.filter(event => event.labelProcessingMetrics?.filesGenerated?.png).length,
  };

  const totalFileSize = events.reduce((sum, event) => {
    return sum + (event.labelProcessingMetrics?.totalFileSize || 0);
  }, 0);

  const averageTimings = {
    zplProcessingTime: 0,
    pdfGenerationTime: 0,
    pngGenerationTime: 0,
    shipiumRequestTime: 0,
    labelaryRequestTime: 0,
  };

  const timeMetrics = [
    { key: 'zplProcessingTime', source: event => event.labelProcessingMetrics?.zplProcessingTime },
    { key: 'pdfGenerationTime', source: event => event.labelProcessingMetrics?.pdfGenerationTime },
    { key: 'pngGenerationTime', source: event => event.labelProcessingMetrics?.pngGenerationTime },
    { key: 'shipiumRequestTime', source: event => event.shipiumRequestTime },
    { key: 'labelaryRequestTime', source: event => event.labelaryRequestTime },
  ];

  timeMetrics.forEach(metric => {
    const values = events
      .map(metric.source)
      .filter(value => typeof value === 'number' && value >= 0);
    averageTimings[metric.key] = values.length > 0
      ? Math.round(values.reduce((acc, value) => acc + value, 0) / values.length)
      : 0;
  });

  return {
    totalEvents,
    successRate: Math.round(successRate * 100) / 100,
    averageResponseTime: Math.round(averageResponseTime * 100) / 100,
    totalFileSize,
    filesGenerated,
    averageTimings,
    endpointBreakdown: endpointBreakdown || {},
  };
};

const Analytics = () => {
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [cancellingLabelId, setCancellingLabelId] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [labelToCancel, setLabelToCancel] = useState(null);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('details');
  const [copiedText, setCopiedText] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    sourceRequest: true,
    sourceResponse: true,
    shipiumRequest: false,
    shipiumResponse: false,
  });
  
  // Filter states
  const [endpointFilter, setEndpointFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [shipiumIdFilter, setShipiumIdFilter] = useState('');
  const [trackingIdFilter, setTrackingIdFilter] = useState('');
  
  // Sorting and pagination states
  const [sortColumn, setSortColumn] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 25, totalPages: 1 });
  
  // Column widths state
  const [columnWidths, setColumnWidths] = useState({
    timestamp: 180,
    endpoint: 220,
    status: 120,
    responseTime: 120,
    shipmentId: 380,
    trackingId: 200,
    carrier: 150,
    files: 200,
  });
  
  // Cache and polling states
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [nextUpdateTime, setNextUpdateTime] = useState(null);
  const [cacheKey, setCacheKey] = useState(null);
  const CACHE_TTL = 10 * 60 * 1000; // 10 minutes - matches server cache
  const POLL_INTERVAL = 10 * 60 * 1000; // 10 minutes for automatic updates
  
  // Client-side cache
  const cacheRef = useRef(new Map());

  const fetchAnalytics = useCallback(async () => {
    console.log('🚀 fetchAnalytics called with dateRange:', dateRange, 'page:', page, 'rowsPerPage:', rowsPerPage);
    try {
      setRefreshing(true);
      setError(null);
      
      // Generate cache key based on date range
      const now = new Date();
      let startDate = null;
      
      switch (dateRange) {
        case '1h':
          startDate = new Date(now.getTime() - 60 * 60 * 1000);
          break;
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'all':
          startDate = null; // No start date filter for "all"
          break;
        default:
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      }
      
      // Create cache key that includes date range, page, pageSize, and all filters
      const timeBucket = Math.floor(now.getTime() / (10 * 60 * 1000)); // 10-minute buckets
      const filterKey = `endpoint:${endpointFilter || 'all'}-status:${statusFilter || 'all'}-carrier:${carrierFilter || 'all'}-shipiumId:${shipiumIdFilter || 'all'}-trackingId:${trackingIdFilter || 'all'}`;
      const newCacheKey = `${dateRange}-${startDate ? startDate.getTime() : 'all'}-${timeBucket}-page:${page + 1}-size:${rowsPerPage}-${filterKey}`;
      
      // Check client-side cache first
      const cached = cacheRef.current.get(newCacheKey);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        console.log('✓ Using cached analytics data (client-side cache)', {
          cacheKey: newCacheKey,
          age: Date.now() - cached.timestamp,
          eventsCount: cached.data.events?.length
        });
        setSummary(cached.data.summary);
        setEvents(cached.data.events);
        setLastUpdateTime(new Date(cached.timestamp));
        setNextUpdateTime(new Date(cached.timestamp + CACHE_TTL));
        setCacheKey(newCacheKey);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      
      console.log('📊 Cache miss or expired, fetching fresh data', {
        cacheKey: newCacheKey,
        cached: !!cached,
        cacheAge: cached ? Date.now() - cached.timestamp : null
      });
      
      setLoading(true);
      setRefreshing(true);

      const params = {
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: now.toISOString(),
        page: page + 1,
        pageSize: rowsPerPage,
        ...(statusFilter && { statusFilter }),
        ...(carrierFilter && { carrierFilter }),
        ...(endpointFilter && { endpointFilter }),
        ...(shipiumIdFilter && { shipiumIdFilter }),
        ...(trackingIdFilter && { trackingIdFilter }),
      };
      
      console.log('📊 Fetching fresh analytics data from server', {
        dateRange,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: now.toISOString(),
        page: page + 1,
        pageSize: rowsPerPage,
        params
      });
      
      const response = await apiClient.get('/analytics', {
        params,
        timeoutMs: 90_000,
      });
      
      if (response && response.data) {
        const eventsData = Array.isArray(response.data.events) ? response.data.events : [];
        const paginationData = response.data.pagination || { total: eventsData.length, page: 1, pageSize: rowsPerPage, totalPages: 1 };
        const endpointBreakdown = response.data.summary?.endpointBreakdown || {};
        const summaryData = response.data.summary || buildSummaryFallback(eventsData, endpointBreakdown);

        // Cache the response
        const cacheData = {
          summary: summaryData,
          events: eventsData,
          timestamp: Date.now(),
        };
        cacheRef.current.set(newCacheKey, cacheData);
        
        console.log('📊 Analytics data received:', {
          eventsCount: response.data.events?.length || 0,
          summary: response.data.summary,
          dateRange: dateRange,
          cacheKey: newCacheKey,
          firstEvent: response.data.events?.[0],
          endpointBreakdown: response.data.summary?.endpointBreakdown
        });
        
        console.log('📊 Endpoint Breakdown Details:', JSON.stringify(response.data.summary?.endpointBreakdown, null, 2));
        
        setSummary(summaryData);
        setEvents(eventsData);
        setPagination(paginationData);
        setLastUpdateTime(new Date());
        setNextUpdateTime(new Date(Date.now() + CACHE_TTL));
        setCacheKey(newCacheKey);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
      const aborted = err?.name === 'AbortError';
      setError(
        aborted
          ? 'Request timed out. Try a narrower date range or paste the full tracking number.'
          : err.response?.data?.message || 'Failed to fetch analytics',
      );
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [CACHE_TTL, apiClient, dateRange, page, rowsPerPage, endpointFilter, statusFilter, carrierFilter, shipiumIdFilter, trackingIdFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
  };

  const handleCopyShipmentId = async (shipmentId) => {
    try {
      await navigator.clipboard.writeText(shipmentId);
      setCopiedId(shipmentId);
      setTimeout(() => setCopiedId(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleCopyTrackingId = async (trackingId) => {
    try {
      await navigator.clipboard.writeText(trackingId);
      setCopiedId(trackingId);
      setTimeout(() => setCopiedId(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleCancelLabelClick = (event) => {
    const shipiumLabelId = event.shipiumLabelId || 
                           event.sourceResponseBody?.carrierLabel?.shipiumLabelId ||
                           event.shipiumResponseBody?.carrierLabel?.shipiumLabelId;
    
    if (!shipiumLabelId) {
      setError('No label ID found for this event');
      return;
    }

    // Store the event and label ID, then show confirmation dialog
    setLabelToCancel({ event, shipiumLabelId });
    setCancelConfirmOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!labelToCancel) {
      setCancelConfirmOpen(false);
      return;
    }

    // Prevent double submission
    if (cancellingLabelId) {
      console.warn('Cancel already in progress, ignoring duplicate request');
      return;
    }

    const { shipiumLabelId } = labelToCancel;
    setCancelConfirmOpen(false);
    setCancellingLabelId(shipiumLabelId);
    setError(null);

    try {
      console.log('[Analytics] Cancelling label:', shipiumLabelId);
      const apiToken = process.env.REACT_APP_API_TOKEN;
      console.log('[Analytics] Using API token:', apiToken ? `${apiToken.substring(0, 20)}...` : 'NOT SET');
      
      const response = await apiClient.post('/api/v1/labels/cancel', {
        shipiumLabelId: shipiumLabelId
      }, {
        useApiToken: true // Use API token instead of user's login token for Shipium API calls
      });

      console.log('Label cancelled successfully:', response.data);
      
      // Show success message
      setError(null);
      alert('Label cancelled successfully!');
      
      // Refresh analytics data to reflect the cancellation
      await fetchAnalytics();
    } catch (err) {
      console.error('Error cancelling label:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to cancel label';
      setError(`Failed to cancel label: ${errorMessage}`);
    } finally {
      setCancellingLabelId(null);
      setLabelToCancel(null);
    }
  };

  const handleCancelDismiss = () => {
    setCancelConfirmOpen(false);
    setLabelToCancel(null);
  };

  const handleCopyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(key);
      setTimeout(() => setCopiedText(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleOpenShipiumReport = (shipmentId) => {
    const url = `https://partner.shipium.com/veyer-legacy/delivery-reporting/shipment/${shipmentId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Helper function to format JSON payloads (handles string or object)
  const formatJsonPayload = (payload) => {
    if (!payload) return null;
    
    try {
      // If it's a string, try to parse it first
      if (typeof payload === 'string') {
        const parsed = JSON.parse(payload);
        return JSON.stringify(parsed, null, 2);
      }
      // If it's already an object, just stringify it
      return JSON.stringify(payload, null, 2);
    } catch (e) {
      // If parsing fails, return the original string
      return payload;
    }
  };

  const handleDownloadFile = async (shipmentId, fileType) => {
    const downloadKey = `${shipmentId}-${fileType}`;
    console.log('=== DOWNLOAD DEBUG START ===');
    console.log('Parameters:', { shipmentId, fileType, downloadKey });
    
    try {
      setDownloadingFile(downloadKey);
      setError(null);
      
      const url = `/api/v1/labels/gcs/${shipmentId}/${fileType.toLowerCase()}`;
      console.log('Request URL:', url);
      console.log('Request config:', { headers: { Accept: 'application/octet-stream' } });
      
      // Make request to server to get the file from Google Cloud Storage
      console.log('Making API client request...');
      const response = await apiClient.get(url, {
        headers: { Accept: 'application/octet-stream' }
      });

      console.log('Response received:', {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        dataType: typeof response.data,
        dataSize: response.data?.size || 'unknown',
        dataConstructor: response.data?.constructor?.name || 'unknown'
      });

      // Create blob URL and trigger download
      console.log('Creating blob...');
      const blob = new Blob([response.data], { 
        type: fileType === 'PDF' ? 'application/pdf' : 
              fileType === 'PNG' ? 'image/png' : 
              'text/plain' 
      });
      console.log('Blob created:', {
        size: blob.size,
        type: blob.type
      });
      
      console.log('Creating object URL...');
      const objectUrl = window.URL.createObjectURL(blob);
      console.log('Object URL created:', objectUrl);
      
      console.log('Creating download link...');
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${shipmentId}.${fileType.toLowerCase()}`;
      link.style.display = 'none';
      
      console.log('Link properties:', {
        href: link.href,
        download: link.download,
        display: link.style.display
      });
      
      console.log('Appending link to DOM...');
      document.body.appendChild(link);
      
      console.log('Triggering click...');
      link.click();
      
      console.log('Setting cleanup timeout...');
      setTimeout(() => {
        console.log('Cleaning up...');
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
        console.log('Cleanup complete');
      }, 100);
      
      console.log('Download triggered successfully');
      console.log('=== DOWNLOAD DEBUG END ===');
    } catch (err) {
      console.error('=== DOWNLOAD ERROR ===');
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      setError(`Failed to download ${fileType} file: ${err.message}`);
      console.log('=== DOWNLOAD ERROR END ===');
    } finally {
      setDownloadingFile(null);
    }
  };

  // Handle status button click to show response body
  const handleStatusClick = (event) => {
    setSelectedEvent(event);
    setResponseDialogOpen(true);
  };

  // Handle export dialog open
  const handleExportClick = () => {
    if (!filteredEvents || filteredEvents.length === 0) {
      setError('No data to export');
      return;
    }
    setExportDialogOpen(true);
  };

  // Handle export based on type
  const handleExport = () => {
    try {
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');

      switch (exportType) {
        case 'summary':
          exportSummaryCSV(timestamp);
          break;
        case 'summary-json':
          exportSummaryJSON(timestamp);
          break;
        case 'details':
          exportDetailsCSV(timestamp);
          break;
        case 'details-json':
          exportDetailsJSON(timestamp);
          break;
        case 'pdf':
          exportPDF(timestamp);
          break;
        case 'png':
          exportPNG(timestamp);
          break;
        default:
          setError('Invalid export type');
          return;
      }

      setExportDialogOpen(false);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export data');
    }
  };

  // Export summary data as CSV
  const exportSummaryCSV = (timestamp) => {
    const headers = [
      'Metric',
      'Value',
      'Description'
    ];

    const csvRows = [
      ['Total Events', summary?.totalEvents || 0, 'Total number of API requests'],
      ['Success Rate', `${summary?.successRate || 0}%`, 'Percentage of successful requests'],
      ['Average Response Time', `${summary?.averageResponseTime || 0}ms`, 'Average response time across all requests'],
      ['Total File Size', `${summary?.totalFileSize || 0} bytes`, 'Total size of generated files'],
      ['ZPL Files Generated', summary?.filesGenerated?.zpl || 0, 'Number of ZPL files generated'],
      ['PDF Files Generated', summary?.filesGenerated?.pdf || 0, 'Number of PDF files generated'],
      ['PNG Files Generated', summary?.filesGenerated?.png || 0, 'Number of PNG files generated'],
      ['Average Memory Usage', `${summary?.performanceMetrics?.averageMemoryUsage || 0} bytes`, 'Average memory usage'],
      ['Peak Memory Usage', `${summary?.performanceMetrics?.peakMemoryUsage || 0} bytes`, 'Peak memory usage'],
      ['Average Concurrent Requests', summary?.performanceMetrics?.averageConcurrentRequests || 0, 'Average concurrent requests']
    ];

    // Add endpoint breakdown
    if (summary?.endpointBreakdown) {
      Object.entries(summary.endpointBreakdown).forEach(([endpoint, data]) => {
        csvRows.push([`${endpoint} - Total Events`, data.totalEvents || 0, `Total events for ${endpoint}`]);
        csvRows.push([`${endpoint} - Success Rate`, `${data.successRate || 0}%`, `Success rate for ${endpoint}`]);
        csvRows.push([`${endpoint} - Avg Response Time`, `${data.averageResponseTime || 0}ms`, `Average response time for ${endpoint}`]);
        csvRows.push([`${endpoint} - Files Generated`, Object.values(data.filesGenerated || {}).reduce((a, b) => a + b, 0), `Total files generated for ${endpoint}`]);
      });
    }

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => 
        row.map(field => {
          const stringField = String(field || '');
          if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `analytics-summary-${timestamp}.csv`);
  };

  // Export summary data as JSON
  const exportSummaryJSON = (timestamp) => {
    const summaryData = {
      exportDate: new Date().toISOString(),
      totalEvents: summary?.totalEvents || 0,
      successRate: summary?.successRate || 0,
      averageResponseTime: summary?.averageResponseTime || 0,
      totalFileSize: summary?.totalFileSize || 0,
      filesGenerated: summary?.filesGenerated || {},
      endpointBreakdown: summary?.endpointBreakdown || {},
      performanceMetrics: summary?.performanceMetrics || {}
    };

    const jsonContent = JSON.stringify(summaryData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    downloadFile(blob, `analytics-summary-${timestamp}.json`);
  };

  // Export detailed CSV
  const exportDetailsCSV = (timestamp) => {
    const headers = [
      'ID',
      'Timestamp',
      'Source Endpoint',
      'Source Method',
      'Shipium Endpoint',
      'Shipium Method',
      'Status',
      'Status Code',
      'Response Time (ms)',
      'Shipium Request Time (ms)',
      'Shipium Shipment ID',
      'Shipium Label ID',
      'Carrier',
      'Error Message',
      'Source Request Body',
      'Source Response Body',
      'Shipium Request Body',
      'Shipium Response Body',
      'Performance Metrics'
    ];

    const csvRows = (filteredEvents || []).map(event => {
      return [
        event.id || '',
        new Date(event.timestamp).toLocaleString(),
        event.sourceEndpoint || 'N/A',
        event.sourceMethod || 'N/A',
        event.shipiumEndpoint || 'N/A',
        event.shipiumMethod || 'N/A',
        event.success ? 'Success' : 'Error',
        event.statusCode || '',
        event.totalRequestTime || 0,
        event.shipiumRequestTime || 0,
        event.shipiumShipmentId || '',
        event.shipiumLabelId || '',
        event.carrierName || event.carrier || '',
        event.error || '',
        JSON.stringify(event.sourceRequestBody || {}),
        JSON.stringify(event.sourceResponseBody || {}),
        JSON.stringify(event.shipiumRequestBody || {}),
        JSON.stringify(event.shipiumResponseBody || {}),
        JSON.stringify(event.performanceMetrics || {})
      ];
    });

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => 
        row.map(field => {
          const stringField = String(field || '');
          if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
            return `"${stringField.replace(/"/g, '""')}"`;
          }
          return stringField;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `analytics-details-${timestamp}.csv`);
  };

  // Export detailed JSON
  const exportDetailsJSON = (timestamp) => {
    const detailsData = {
      exportDate: new Date().toISOString(),
      totalEvents: (filteredEvents || []).length,
      events: (filteredEvents || []).map(event => ({
        id: event.id,
        timestamp: event.timestamp,
        sourceEndpoint: event.sourceEndpoint,
        sourceMethod: event.sourceMethod,
        shipiumEndpoint: event.shipiumEndpoint,
        shipiumMethod: event.shipiumMethod,
        success: event.success,
        statusCode: event.statusCode,
        totalRequestTime: event.totalRequestTime,
        shipiumRequestTime: event.shipiumRequestTime,
        shipiumShipmentId: event.shipiumShipmentId,
        shipiumLabelId: event.shipiumLabelId,
        error: event.error,
        sourceRequestBody: event.sourceRequestBody,
        sourceResponseBody: event.sourceResponseBody,
        shipiumRequestBody: event.shipiumRequestBody,
        shipiumResponseBody: event.shipiumResponseBody,
        performanceMetrics: event.performanceMetrics
      }))
    };

    const jsonContent = JSON.stringify(detailsData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    downloadFile(blob, `analytics-details-${timestamp}.json`);
  };

  // Export PDF report
  const exportPDF = async (timestamp) => {
    try {
      // Create a temporary container for the report
      const reportContainer = document.createElement('div');
      reportContainer.style.position = 'absolute';
      reportContainer.style.left = '-9999px';
      reportContainer.style.top = '0';
      reportContainer.style.width = '1200px';
      reportContainer.style.backgroundColor = '#ffffff';
      reportContainer.style.padding = '40px';
      reportContainer.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(reportContainer);

      // Generate the report content
      const reportHTML = generatePDFReportHTML();
      reportContainer.innerHTML = reportHTML;

      // Wait for any images or charts to load
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Convert to canvas and then to PDF
      const canvas = await html2canvas(reportContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Clean up
      document.body.removeChild(reportContainer);

      // Download the PDF
      pdf.save(`analytics-report-${timestamp}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error generating PDF report. Please try again.');
    }
  };

  // Export PNG report
  const exportPNG = async (timestamp) => {
    try {
      // Create a temporary container for the report
      const reportContainer = document.createElement('div');
      reportContainer.style.position = 'absolute';
      reportContainer.style.left = '-9999px';
      reportContainer.style.top = '0';
      reportContainer.style.width = '1200px';
      reportContainer.style.backgroundColor = '#ffffff';
      reportContainer.style.padding = '40px';
      reportContainer.style.fontFamily = 'Arial, sans-serif';
      document.body.appendChild(reportContainer);

      // Generate the report content
      const reportHTML = generatePNGReportHTML();
      reportContainer.innerHTML = reportHTML;

      // Wait for any images or charts to load
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Convert to canvas
      const canvas = await html2canvas(reportContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics-report-${timestamp}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');

      // Clean up
      document.body.removeChild(reportContainer);
    } catch (error) {
      console.error('Error generating PNG:', error);
      setError('Error generating PNG report. Please try again.');
    }
  };

  // Chart data generation removed as it's not currently used

  // Generate PDF report HTML with visual elements
  const generatePDFReportHTML = () => {
    const timestamp = new Date().toLocaleString();
    
    return generateSimpleReportHTML(timestamp);
  };

  // Generate PNG report HTML with visual elements
  const generatePNGReportHTML = () => {
    const timestamp = new Date().toLocaleString();
    
    return generateSimpleReportHTML(timestamp);
  };

  // Generate simple report without charts
  const generateSimpleReportHTML = (timestamp) => {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Analytics Report - ${timestamp}</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
            color: #333;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            padding: 20px;
            background: linear-gradient(135deg, #1976d2, #42a5f5);
            color: white;
            border-radius: 8px;
        }
        .header h1 {
            margin: 0;
            font-size: 28px;
            font-weight: 300;
        }
        .header p {
            margin: 5px 0 0 0;
            opacity: 0.9;
        }
        .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
            margin-bottom: 30px;
        }
        .summary-card {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            border-left: 4px solid #1976d2;
        }
        .summary-card h3 {
            margin: 0 0 10px 0;
            color: #1976d2;
            font-size: 16px;
            font-weight: 500;
        }
        .summary-card .value {
            font-size: 24px;
            font-weight: bold;
            color: #333;
            margin-bottom: 5px;
        }
        .summary-card .label {
            font-size: 12px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .endpoint-breakdown {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .endpoint-breakdown h2 {
            margin: 0 0 20px 0;
            color: #1976d2;
            font-size: 20px;
            font-weight: 500;
        }
        .endpoint-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
        }
        .endpoint-table th,
        .endpoint-table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #eee;
        }
        .endpoint-table th {
            background-color: #f5f5f5;
            font-weight: 500;
            color: #333;
        }
        .endpoint-table tr:hover {
            background-color: #f9f9f9;
        }
        .success-rate {
            color: #4caf50;
            font-weight: bold;
        }
        .error-rate {
            color: #f44336;
            font-weight: bold;
        }
        .footer {
            text-align: center;
            margin-top: 30px;
            padding: 20px;
            color: #666;
            font-size: 12px;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>Analytics Dashboard Report</h1>
        <p>Generated on ${timestamp}</p>
    </div>

    <div class="summary-grid">
        <div class="summary-card">
            <h3>Total Requests</h3>
            <div class="value">${summary?.totalEvents || 0}</div>
            <div class="label">All Time</div>
        </div>
        <div class="summary-card">
            <h3>Success Rate</h3>
            <div class="value">${summary?.successRate || 0}%</div>
            <div class="label">Overall</div>
        </div>
        <div class="summary-card">
            <h3>Average Response Time</h3>
            <div class="value">${formatDuration(summary?.averageResponseTime || 0)}</div>
            <div class="label">All Endpoints</div>
        </div>
        <div class="summary-card">
            <h3>Files Generated</h3>
            <div class="value">${summary?.filesGenerated ? Object.values(summary.filesGenerated).reduce((a, b) => a + b, 0) : 0}</div>
            <div class="label">Total Files</div>
        </div>
    </div>

    <div class="endpoint-breakdown">
        <h2>Endpoint Performance Breakdown</h2>
        <table class="endpoint-table">
            <thead>
                <tr>
                    <th>Endpoint</th>
                    <th>Total Requests</th>
                    <th>Success Rate</th>
                    <th>Avg Response Time</th>
                    <th>Files Generated</th>
                </tr>
            </thead>
            <tbody>
                ${summary?.endpointBreakdown ? Object.entries(summary.endpointBreakdown).map(([endpoint, data]) => `
                <tr>
                    <td><strong>${endpoint}</strong></td>
                    <td>${data.totalEvents || 0}</td>
                    <td><span class="${data.successRate >= 90 ? 'success-rate' : 'error-rate'}">${data.successRate || 0}%</span></td>
                    <td>${formatDuration(data.averageResponseTime || 0)}</td>
                    <td>${data.filesGenerated ? Object.values(data.filesGenerated).reduce((a, b) => a + b, 0) : 0}</td>
                </tr>
                `).join('') : ''}
            </tbody>
        </table>
    </div>

    <div class="footer">
        <p>Report generated by Veyer FedEx Middleware Analytics Dashboard</p>
    </div>
</body>
</html>`;
  };

  // Generate HTML report content
  const generateReportHTML = () => {
    return `
<!DOCTYPE html>
<html>
<head>
    <title>Analytics Report</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .card { border: 1px solid #ddd; margin: 10px 0; padding: 15px; border-radius: 5px; }
        .metric { display: inline-block; margin: 10px; padding: 10px; background: #f5f5f5; border-radius: 3px; }
        .endpoint-breakdown { margin: 20px 0; }
        .endpoint-card { border: 1px solid #ccc; margin: 10px 0; padding: 10px; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
    </style>
</head>
<body>
    <div class="header">
        <h1>Analytics Report</h1>
        <p>Generated on: ${new Date().toLocaleString()}</p>
    </div>
    
    <div class="card">
        <h2>Summary Metrics</h2>
        <div class="metric">Total Events: ${summary?.totalEvents || 0}</div>
        <div class="metric">Success Rate: ${summary?.successRate || 0}%</div>
        <div class="metric">Avg Response Time: ${summary?.averageResponseTime || 0}ms</div>
        <div class="metric">Total File Size: ${formatFileSize(summary?.totalFileSize || 0)}</div>
    </div>
    
    <div class="endpoint-breakdown">
        <h2>Endpoint Performance Breakdown</h2>
        ${Object.entries(summary?.endpointBreakdown || {}).map(([endpoint, data]) => `
            <div class="endpoint-card">
                <h3>${endpoint}</h3>
                <p>Total Events: ${data.totalEvents}</p>
                <p>Success Rate: ${data.successRate}%</p>
                <p>Avg Response Time: ${data.averageResponseTime}ms</p>
                <p>Files Generated: ZPL(${data.filesGenerated?.zpl || 0}) PDF(${data.filesGenerated?.pdf || 0}) PNG(${data.filesGenerated?.png || 0})</p>
            </div>
        `).join('')}
    </div>
    
    <div class="card">
        <h2>Recent Events (${filteredEvents?.length || 0} shown)</h2>
        <table>
            <thead>
                <tr>
                    <th>Timestamp</th>
                    <th>Endpoint</th>
                    <th>Status</th>
                    <th>Response Time</th>
                    <th>Shipment ID</th>
                </tr>
            </thead>
            <tbody>
                ${(filteredEvents || []).slice(0, 50).map(event => `
                    <tr>
                        <td>${new Date(event.timestamp).toLocaleString()}</td>
                        <td>${event.sourceEndpoint || 'N/A'}</td>
                        <td>${event.success ? 'Success' : 'Error'}</td>
                        <td>${event.totalRequestTime || 0}ms</td>
                        <td>${event.shipiumShipmentId || ''}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    </div>
</body>
</html>`;
  };

  // Helper function to download file
  const downloadFile = (blob, filename) => {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Filter events - REMOVED

  // Get endpoint type from endpoint path
  const getEndpointType = (event) => {
    const sourceEndpoint = event.sourceEndpoint;
    if (!sourceEndpoint || typeof sourceEndpoint !== 'string') return 'Other';
    
    if (sourceEndpoint.includes('/labels/create')) return 'Label Management';
    if (sourceEndpoint.includes('/carrierselection/rateshop') || 
        (sourceEndpoint.includes('/shipment/carrierselection/label') && 
         event.shipiumRequestBody?.generateLabel === false)) return 'Rate Shopping';
    if (sourceEndpoint.includes('/address/')) return 'Address Validation';
    return 'Other';
  };

  // Clear all filters - REMOVED

  // Handle column resize
  const handleColumnResize = (columnKey, width) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: width
    }));
  };

  // Handle mouse down for resizing
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey];

    const handleMouseMove = (e) => {
      const newWidth = startWidth + (e.clientX - startX);
      const minWidth = 80;
      const maxWidth = 600;
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        handleColumnResize(columnKey, newWidth);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Sorting function
  const applySorting = useCallback((eventsToSort) => {
    if (!eventsToSort || !Array.isArray(eventsToSort)) {
      return [];
    }
    
    const sorted = [...eventsToSort].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortColumn) {
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case 'endpoint':
          aValue = a.sourceEndpoint || '';
          bValue = b.sourceEndpoint || '';
          break;
        case 'status':
          aValue = a.success ? 1 : 0;
          bValue = b.success ? 1 : 0;
          break;
        case 'responseTime':
          aValue = a.totalRequestTime || 0;
          bValue = b.totalRequestTime || 0;
          break;
        case 'shipmentId':
          aValue = a.shipiumShipmentId || '';
          bValue = b.shipiumShipmentId || '';
          break;
        case 'trackingId':
          aValue = a.sourceResponseBody?.carrierLabel?.carrierTrackingId || 
                   a.shipiumResponseBody?.carrierLabel?.carrierTrackingId ||
                   a.sourceResponseBody?.trackingNumber || 
                   a.shipiumResponseBody?.trackingNumber ||
                   a.sourceResponseBody?.tracking?.trackingNumber ||
                   a.shipiumResponseBody?.tracking?.trackingNumber || '';
          bValue = b.sourceResponseBody?.carrierLabel?.carrierTrackingId || 
                   b.shipiumResponseBody?.carrierLabel?.carrierTrackingId ||
                   b.sourceResponseBody?.trackingNumber || 
                   b.shipiumResponseBody?.trackingNumber ||
                   b.sourceResponseBody?.tracking?.trackingNumber ||
                   b.shipiumResponseBody?.tracking?.trackingNumber || '';
          break;
        case 'carrier':
          aValue = a.sourceResponseBody?.carrierLabel?.carrier || 
                   a.shipiumResponseBody?.carrierLabel?.carrier ||
                   a.sourceResponseBody?.carrier || 
                   a.shipiumResponseBody?.carrier ||
                   a.sourceResponseBody?.carrierId ||
                   a.shipiumResponseBody?.carrierId || '';
          bValue = b.sourceResponseBody?.carrierLabel?.carrier || 
                   b.shipiumResponseBody?.carrierLabel?.carrier ||
                   b.sourceResponseBody?.carrier || 
                   b.shipiumResponseBody?.carrier ||
                   b.sourceResponseBody?.carrierId ||
                   b.shipiumResponseBody?.carrierId || '';
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortDirection === 'asc' 
        ? aValue - bValue
        : bValue - aValue;
    });
    
    return sorted;
  }, [sortColumn, sortDirection]);

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortColumn(column);
      setSortDirection('desc');
    }
    // Reset to first page when sorting changes
    setPage(0);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // useEffect will trigger fetchAnalytics when page changes
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when page size changes
    // useEffect will trigger fetchAnalytics when rowsPerPage changes
  };

  // Apply sorting when values change (no filtering)
  useEffect(() => {
    if (!events) {
      setFilteredEvents([]);
      return;
    }
    
    const sorted = applySorting(events);
    setFilteredEvents(sorted);
  }, [events, sortColumn, sortDirection, applySorting]);

  // Reset page to 0 when any filter changes
  useEffect(() => {
    setPage(0);
  }, [endpointFilter, statusFilter, carrierFilter, shipiumIdFilter, trackingIdFilter]);

  // Initial fetch when date range, page, pageSize, or filters change
  useEffect(() => {
    // Clear cache when date range, page, pageSize, or filters change to force fresh data
    console.log('🔄 Date range, page, pageSize, or filters changed, fetching fresh data', { 
      dateRange, 
      page, 
      rowsPerPage,
      endpointFilter,
      statusFilter,
      carrierFilter,
      shipiumIdFilter,
      trackingIdFilter
    });
    cacheRef.current.clear();
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, page, rowsPerPage, endpointFilter, statusFilter, carrierFilter, shipiumIdFilter, trackingIdFilter]);

  // Set up periodic polling (10 minutes)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      console.log('⏰ Automatic 10-minute refresh triggered');
      fetchAnalytics();
    }, POLL_INTERVAL);
    
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [POLL_INTERVAL]);

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getStatusColor = (success) => {
    return success ? 'success' : 'error';
  };

  const getStatusIcon = (success) => {
    return success ? <TrendingUp /> : <TrendingDown />;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Add null check for summary to prevent filter errors
  console.log('🎨 Analytics component state:', {
    summary: summary,
    endpointBreakdown: summary?.endpointBreakdown,
    endpointBreakdownKeys: summary?.endpointBreakdown ? Object.keys(summary.endpointBreakdown) : [],
    eventsCount: events?.length || 0,
    filteredEventsCount: filteredEvents?.length || 0,
    loading,
    error
  });

  const hasEvents = Array.isArray(events) && events.length > 0;

  if (!summary || !hasEvents) {
    return (
      <Box sx={{ p: 3, textAlign: 'left' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 3 }}>
          <AnalyticsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4">Analytics Dashboard</Typography>
        </Box>
        <Alert
          severity={error ? 'error' : 'info'}
          action={
            error ? (
              <Button color="inherit" size="small" onClick={handleRefresh} disabled={refreshing}>
                Retry
              </Button>
            ) : null
          }
        >
          {error
            ? error
            : 'No analytics data available for the selected timeframe. Try adjusting the filters or refresh to fetch new events.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, textAlign: 'left' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <AnalyticsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4">Analytics Dashboard</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="1h">Last Hour</MenuItem>
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>
          
          {/* Last Update Info */}
          {lastUpdateTime && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'flex-end',
              fontSize: '0.85rem',
              color: 'text.secondary',
              minWidth: 200
            }}>
              <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                Last updated: {lastUpdateTime.toLocaleTimeString()}
              </Typography>
              {nextUpdateTime && (
                <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                  Next update: {nextUpdateTime.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
          )}
          
          <Tooltip title={refreshing ? 'Refreshing...' : 'Manually refresh data now'}>
            <span>
              <IconButton 
                onClick={fetchAnalytics} 
                disabled={refreshing}
                sx={{ 
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  }
                }}
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
            size="small"
            onClick={handleExportClick}
            disabled={!filteredEvents || filteredEvents.length === 0}
          >
            Export
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {summary && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Total Requests
                      </Typography>
                      <Typography variant="h4">
                        {summary.totalEvents.toLocaleString()}
                      </Typography>
                    </Box>
                    <LocalShipping color="primary" sx={{ fontSize: 40, ml: 2 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Success Rate
                      </Typography>
                      <Typography variant="h4">
                        {summary.successRate}%
                      </Typography>
                    </Box>
                    <Speed color="success" sx={{ fontSize: 40, ml: 2 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Avg Response Time
                      </Typography>
                      <Typography variant="h4">
                        {formatDuration(summary.averageResponseTime)}
                      </Typography>
                    </Box>
                    <TrendingUp color="info" sx={{ fontSize: 40, ml: 2 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
      <Card>
        <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>
                        Total File Size
                      </Typography>
                      <Typography variant="h4">
                        {formatFileSize(summary.totalFileSize)}
                      </Typography>
                    </Box>
                    <Storage color="warning" sx={{ fontSize: 40, ml: 2 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Performance Metrics */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader title="File Generation Stats" />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">ZPL Files</Typography>
                      <Typography variant="body2">{summary.filesGenerated.zpl}</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(summary.filesGenerated.zpl / summary.totalEvents) * 100} 
                      sx={{ mb: 2 }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">PDF Files</Typography>
                      <Typography variant="body2">{summary.filesGenerated.pdf}</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(summary.filesGenerated.pdf / summary.totalEvents) * 100} 
                      sx={{ mb: 2 }}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">PNG Files</Typography>
                      <Typography variant="body2">{summary.filesGenerated.png}</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(summary.filesGenerated.png / summary.totalEvents) * 100} 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardHeader title="Average Processing Times" />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {summary.averageTimings && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">ZPL Processing</Typography>
                          <Typography variant="body2">{formatDuration(summary.averageTimings.zplProcessingTime)}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((summary.averageTimings.zplProcessingTime / 1000) * 100, 100)} 
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">PDF Generation</Typography>
                          <Typography variant="body2">{formatDuration(summary.averageTimings.pdfGenerationTime)}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((summary.averageTimings.pdfGenerationTime / 2000) * 100, 100)} 
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">PNG Generation</Typography>
                          <Typography variant="body2">{formatDuration(summary.averageTimings.pngGenerationTime)}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((summary.averageTimings.pngGenerationTime / 2000) * 100, 100)} 
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Shipium API</Typography>
                          <Typography variant="body2">{formatDuration(summary.averageTimings.shipiumRequestTime)}</Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((summary.averageTimings.shipiumRequestTime / 5000) * 100, 100)} 
                        />
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Endpoint Performance Breakdown */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Endpoint Performance Breakdown" />
                <CardContent>
                  {console.log('🔍 Rendering Endpoint Breakdown - summary:', summary, 'endpointBreakdown:', summary?.endpointBreakdown)}
                  <Grid container spacing={3}>
                      {/* Rate Shop */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                              Rate Shop
                            </Typography>
                            
                            {/* Two-column layout: Metrics on left, Chart on right */}
                            <Grid container spacing={2}>
                              {/* Left column: Metrics */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Total Requests: {summary.endpointBreakdown?.['Rate Shopping']?.totalEvents || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success Rate: {summary.endpointBreakdown?.['Rate Shopping']?.successRate || 0}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Avg Response: {formatDuration(summary.endpointBreakdown?.['Rate Shopping']?.averageResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  Avg Success: {formatDuration(summary.endpointBreakdown?.['Rate Shopping']?.averageSuccessfulResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                  Avg Error: {formatDuration(summary.endpointBreakdown?.['Rate Shopping']?.averageErrorResponseTime || 0)}
                                </Typography>
                              </Grid>
                              
                              {/* Right column: Pie Chart */}
                              <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  height: '100%'
                                }}>
                                  <Box sx={{ width: '100%', maxWidth: 200 }}>
                                    <Pie
                                      data={{
                                        labels: ['Success', 'Error'],
                                        datasets: [{
                                          data: [
                                            summary.endpointBreakdown?.['Rate Shopping']?.successfulEvents || 0,
                                            summary.endpointBreakdown?.['Rate Shopping']?.errorEvents || 0
                                          ],
                                          backgroundColor: [
                                            'rgba(75, 192, 192, 0.8)',
                                            'rgba(255, 99, 132, 0.8)',
                                          ],
                                          borderColor: [
                                            'rgba(75, 192, 192, 1)',
                                            'rgba(255, 99, 132, 1)',
                                          ],
                                          borderWidth: 1,
                                        }],
                                      }}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                          legend: {
                                            position: 'bottom',
                                            labels: {
                                              boxWidth: 15,
                                              padding: 10,
                                              font: {
                                                size: 11
                                              }
                                            }
                                          },
                                          tooltip: {
                                            callbacks: {
                                              label: function(context) {
                                                const label = context.label || '';
                                                const value = context.parsed || 0;
                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                return `${label}: ${value} (${percentage}%)`;
                                              }
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Label Generate */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                              Label Generate Performance
                            </Typography>
                            
                            {/* Two-column layout: Metrics on left, Chart on right */}
                            <Grid container spacing={2}>
                              {/* Left column: Metrics */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Total Requests: {summary.endpointBreakdown?.['Label Management']?.totalEvents || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success Rate: {summary.endpointBreakdown?.['Label Management']?.successRate || 0}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Avg Response: {formatDuration(summary.endpointBreakdown?.['Label Management']?.averageResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  Avg Success: {formatDuration(summary.endpointBreakdown?.['Label Management']?.averageSuccessfulResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                  Avg Error: {formatDuration(summary.endpointBreakdown?.['Label Management']?.averageErrorResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  File Size: {formatFileSize(summary.endpointBreakdown?.['Label Management']?.totalFileSize || 0)}
                                </Typography>
                              </Grid>
                              
                              {/* Right column: Pie Chart (spans the height of metrics) */}
                              <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  height: '100%'
                                }}>
                                  <Box sx={{ width: '100%', maxWidth: 200 }}>
                                    <Pie
                                      data={{
                                        labels: ['Success', 'Error'],
                                        datasets: [{
                                          data: [
                                            summary.endpointBreakdown?.['Label Management']?.successfulEvents || 0,
                                            summary.endpointBreakdown?.['Label Management']?.errorEvents || 0
                                          ],
                                          backgroundColor: [
                                            'rgba(75, 192, 192, 0.8)',
                                            'rgba(255, 99, 132, 0.8)',
                                          ],
                                          borderColor: [
                                            'rgba(75, 192, 192, 1)',
                                            'rgba(255, 99, 132, 1)',
                                          ],
                                          borderWidth: 1,
                                        }],
                                      }}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                          legend: {
                                            position: 'bottom',
                                            labels: {
                                              boxWidth: 15,
                                              padding: 10,
                                              font: {
                                                size: 11
                                              }
                                            }
                                          },
                                          tooltip: {
                                            callbacks: {
                                              label: function(context) {
                                                const label = context.label || '';
                                                const value = context.parsed || 0;
                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                return `${label}: ${value} (${percentage}%)`;
                                              }
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                            
                            {/* Files Generated - Full width below both columns */}
                            <Divider sx={{ my: 2 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Files Generated:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {(summary.endpointBreakdown?.['Label Management']?.filesGenerated?.zpl || 0) > 0 && (
                                  <Chip 
                                    label={`ZPL: ${summary.endpointBreakdown['Label Management'].filesGenerated.zpl}`} 
                                    size="small" 
                                    color="primary" 
                                  />
                                )}
                                {(summary.endpointBreakdown?.['Label Management']?.filesGenerated?.pdf || 0) > 0 && (
                                  <Chip 
                                    label={`PDF: ${summary.endpointBreakdown['Label Management'].filesGenerated.pdf}`} 
                                    size="small" 
                                    color="secondary" 
                                  />
                                )}
                                {(summary.endpointBreakdown?.['Label Management']?.filesGenerated?.png || 0) > 0 && (
                                  <Chip 
                                    label={`PNG: ${summary.endpointBreakdown['Label Management'].filesGenerated.png}`} 
                                    size="small" 
                                    color="success" 
                                  />
                                )}
                                {((summary.endpointBreakdown?.['Label Management']?.filesGenerated?.zpl || 0) === 0 && 
                                  (summary.endpointBreakdown?.['Label Management']?.filesGenerated?.pdf || 0) === 0 && 
                                  (summary.endpointBreakdown?.['Label Management']?.filesGenerated?.png || 0) === 0) && (
                                  <Typography variant="body2" color="text.secondary">
                                    No files generated
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Address Validation */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                              Address Validation
                            </Typography>
                            
                            {/* Two-column layout: Metrics on left, Chart on right */}
                            <Grid container spacing={2}>
                              {/* Left column: Metrics */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary">
                                  Total Requests: {summary.endpointBreakdown?.['Address Validation']?.totalEvents || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success Rate: {summary.endpointBreakdown?.['Address Validation']?.successRate || 0}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Avg Response: {formatDuration(summary.endpointBreakdown?.['Address Validation']?.averageResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  Avg Success: {formatDuration(summary.endpointBreakdown?.['Address Validation']?.averageSuccessfulResponseTime || 0)}
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                  Avg Error: {formatDuration(summary.endpointBreakdown?.['Address Validation']?.averageErrorResponseTime || 0)}
                                </Typography>
                              </Grid>
                              
                              {/* Right column: Pie Chart */}
                              <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  height: '100%'
                                }}>
                                  <Box sx={{ width: '100%', maxWidth: 200 }}>
                                    <Pie
                                      data={{
                                        labels: ['Success', 'Error'],
                                        datasets: [{
                                          data: [
                                            summary.endpointBreakdown?.['Address Validation']?.successfulEvents || 0,
                                            summary.endpointBreakdown?.['Address Validation']?.errorEvents || 0
                                          ],
                                          backgroundColor: [
                                            'rgba(75, 192, 192, 0.8)',
                                            'rgba(255, 99, 132, 0.8)',
                                          ],
                                          borderColor: [
                                            'rgba(75, 192, 192, 1)',
                                            'rgba(255, 99, 132, 1)',
                                          ],
                                          borderWidth: 1,
                                        }],
                                      }}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                          legend: {
                                            position: 'bottom',
                                            labels: {
                                              boxWidth: 15,
                                              padding: 10,
                                              font: {
                                                size: 11
                                              }
                                            }
                                          },
                                          tooltip: {
                                            callbacks: {
                                              label: function(context) {
                                                const label = context.label || '';
                                                const value = context.parsed || 0;
                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                return `${label}: ${value} (${percentage}%)`;
                                              }
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Label Operations (Reprint + Cancel) */}
                      <Grid item xs={12} sm={6} md={3}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" gutterBottom>
                              Label Operations
                            </Typography>
                            
                            {/* Overall Stats with Pie Chart */}
                            <Grid container spacing={2}>
                              {/* Left column: Overall Metrics */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                  Overall
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Total: {summary.endpointBreakdown?.['Label Operations']?.totalEvents || 0}
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                  Success: {summary.endpointBreakdown?.['Label Operations']?.successfulEvents || 0}
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                  Error: {summary.endpointBreakdown?.['Label Operations']?.errorEvents || 0}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success Rate: {summary.endpointBreakdown?.['Label Operations']?.successRate || 0}%
                                </Typography>
                              </Grid>
                              
                              {/* Right column: Overall Pie Chart */}
                              <Grid item xs={12} md={6}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  height: '100%'
                                }}>
                                  <Box sx={{ width: '100%', maxWidth: 150 }}>
                                    <Pie
                                      data={{
                                        labels: ['Success', 'Error'],
                                        datasets: [{
                                          data: [
                                            summary.endpointBreakdown?.['Label Operations']?.successfulEvents || 0,
                                            summary.endpointBreakdown?.['Label Operations']?.errorEvents || 0
                                          ],
                                          backgroundColor: [
                                            'rgba(75, 192, 192, 0.8)',
                                            'rgba(255, 99, 132, 0.8)',
                                          ],
                                          borderColor: [
                                            'rgba(75, 192, 192, 1)',
                                            'rgba(255, 99, 132, 1)',
                                          ],
                                          borderWidth: 1,
                                        }],
                                      }}
                                      options={{
                                        responsive: true,
                                        maintainAspectRatio: true,
                                        plugins: {
                                          legend: {
                                            position: 'bottom',
                                            labels: {
                                              boxWidth: 12,
                                              padding: 8,
                                              font: {
                                                size: 10
                                              }
                                            }
                                          },
                                          tooltip: {
                                            callbacks: {
                                              label: function(context) {
                                                const label = context.label || '';
                                                const value = context.parsed || 0;
                                                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                return `${label}: ${value} (${percentage}%)`;
                                              }
                                            }
                                          }
                                        }
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                            
                            {/* Sub-breakdown */}
                            {summary.endpointBreakdown?.['Label Operations']?.subBreakdown && (
                              <>
                                <Divider sx={{ my: 2 }} />
                                <Grid container spacing={2}>
                                  {/* Label Reprint */}
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Label Reprint
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                        Total: {summary.endpointBreakdown['Label Operations'].subBreakdown.reprint?.totalEvents || 0}
                                      </Typography>
                                      <Typography variant="body2" color="success.main" sx={{ fontSize: '0.85rem' }}>
                                        Success: {summary.endpointBreakdown['Label Operations'].subBreakdown.reprint?.successfulEvents || 0}
                                      </Typography>
                                      <Typography variant="body2" color="error.main" sx={{ fontSize: '0.85rem' }}>
                                        Error: {summary.endpointBreakdown['Label Operations'].subBreakdown.reprint?.errorEvents || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  
                                  {/* Label Cancel/Void */}
                                  <Grid item xs={12} md={6}>
                                    <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                        Label Cancel/Void
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                        Total: {summary.endpointBreakdown['Label Operations'].subBreakdown.cancel?.totalEvents || 0}
                                      </Typography>
                                      <Typography variant="body2" color="success.main" sx={{ fontSize: '0.85rem' }}>
                                        Success: {summary.endpointBreakdown['Label Operations'].subBreakdown.cancel?.successfulEvents || 0}
                                      </Typography>
                                      <Typography variant="body2" color="error.main" sx={{ fontSize: '0.85rem' }}>
                                        Error: {summary.endpointBreakdown['Label Operations'].subBreakdown.cancel?.errorEvents || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Other */}
                      {summary?.endpointBreakdown && Object.entries(summary.endpointBreakdown)
                        .filter(([endpointType]) => 
                          endpointType !== 'Rate Shopping' && 
                          endpointType !== 'Label Management' && 
                          endpointType !== 'Address Validation' &&
                          endpointType !== 'Label Operations'
                        )
                        .map(([endpointType, data]) => (
                        <Grid item xs={12} sm={6} md={3} key={endpointType}>
                          <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>
                                {endpointType}
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Total Requests: {data.totalEvents}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Success Rate: {data.successRate}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Avg Response: {formatDuration(data.averageResponseTime)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  File Size: {formatFileSize(data.totalFileSize)}
                                </Typography>
                              </Box>
                              <Divider sx={{ my: 1 }} />
                              <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Files Generated:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {data.filesGenerated.zpl > 0 && (
                                    <Chip 
                                      label={`ZPL: ${data.filesGenerated.zpl}`} 
                                      size="small" 
                                      color="primary" 
                                    />
                                  )}
                                  {data.filesGenerated.pdf > 0 && (
                                    <Chip 
                                      label={`PDF: ${data.filesGenerated.pdf}`} 
                                      size="small" 
                                      color="secondary" 
                                    />
                                  )}
                                  {data.filesGenerated.png > 0 && (
                                    <Chip 
                                      label={`PNG: ${data.filesGenerated.png}`} 
                                      size="small" 
                                      color="success" 
                                    />
                                  )}
                                  {data.filesGenerated.zpl === 0 && 
                                   data.filesGenerated.pdf === 0 && 
                                   data.filesGenerated.png === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                      No files generated
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

          {/* Recent Events Table */}
          <Card>
            <CardHeader 
              title="Recent Events" 
              action={
                <IconButton onClick={handleRefresh} disabled={refreshing} title="Refresh">
                  {refreshing ? <CircularProgress size={20} /> : <Refresh />}
                </IconButton>
              }
            />
            <CardContent>
              {/* Filter Section */}
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Endpoint</InputLabel>
                  <Select
                    value={endpointFilter}
                    label="Endpoint"
                    onChange={(e) => setEndpointFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Label Management">Label Management</MenuItem>
                    <MenuItem value="Rate Shopping">Rate Shopping</MenuItem>
                    <MenuItem value="Address Validation">Address Validation</MenuItem>
                    <MenuItem value="Label Operations">Label Operations</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="success">Success</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Carrier</InputLabel>
                  <Select
                    value={carrierFilter}
                    label="Carrier"
                    onChange={(e) => setCarrierFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="fedex">FedEx</MenuItem>
                    <MenuItem value="uspsdirect">USPS Direct</MenuItem>
                    <MenuItem value="ups">UPS</MenuItem>
                    <MenuItem value="lasership">LaserShip</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  size="small"
                  label="Shipium ID"
                  value={shipiumIdFilter}
                  onChange={(e) => setShipiumIdFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                
                <TextField
                  size="small"
                  label="Tracking ID"
                  value={trackingIdFilter}
                  onChange={(e) => setTrackingIdFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Clear />}
                  onClick={() => {
                    setEndpointFilter('');
                    setStatusFilter('');
                    setCarrierFilter('');
                    setShipiumIdFilter('');
                    setTrackingIdFilter('');
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table style={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        style={{ 
                          width: columnWidths.timestamp, 
                          minWidth: 150,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'timestamp'}
                          direction={sortColumn === 'timestamp' ? sortDirection : 'desc'}
                          onClick={() => handleSort('timestamp')}
                        >
                          Timestamp
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'timestamp')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.endpoint, 
                          minWidth: 200,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'endpoint'}
                          direction={sortColumn === 'endpoint' ? sortDirection : 'desc'}
                          onClick={() => handleSort('endpoint')}
                        >
                          Endpoint
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'endpoint')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.status, 
                          minWidth: 100,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'status'}
                          direction={sortColumn === 'status' ? sortDirection : 'desc'}
                          onClick={() => handleSort('status')}
                        >
                          Status
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'status')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.responseTime, 
                          minWidth: 100,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'responseTime'}
                          direction={sortColumn === 'responseTime' ? sortDirection : 'desc'}
                          onClick={() => handleSort('responseTime')}
                        >
                          Response Time
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'responseTime')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.shipmentId, 
                          minWidth: 150,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'shipmentId'}
                          direction={sortColumn === 'shipmentId' ? sortDirection : 'desc'}
                          onClick={() => handleSort('shipmentId')}
                        >
                          Shipium ID
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'shipmentId')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.trackingId, 
                          minWidth: 150,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'trackingId'}
                          direction={sortColumn === 'trackingId' ? sortDirection : 'desc'}
                          onClick={() => handleSort('trackingId')}
                        >
                          Tracking ID
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'trackingId')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.carrier, 
                          minWidth: 120,
                          position: 'relative',
                        }}
                      >
                        <TableSortLabel
                          active={sortColumn === 'carrier'}
                          direction={sortColumn === 'carrier' ? sortDirection : 'desc'}
                          onClick={() => handleSort('carrier')}
                        >
                          Carrier
                        </TableSortLabel>
                        <div 
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '4px',
                            cursor: 'col-resize',
                            backgroundColor: 'transparent'
                          }}
                          onMouseDown={(e) => handleMouseDown(e, 'carrier')}
                        />
                      </TableCell>
                      <TableCell 
                        style={{ 
                          width: columnWidths.files, 
                          minWidth: 150,
                          position: 'relative'
                        }}
                      >
                        Files Generated
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (!filteredEvents || filteredEvents.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                          <CircularProgress size={40} />
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading events from database...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (!filteredEvents || filteredEvents.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                          <Typography variant="body2" color="text.secondary">
                            No events found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredEvents.map((event) => (
                        <TableRow key={event.id} hover>
                        <TableCell style={{ width: columnWidths.timestamp }}>
                          <Typography variant="body2">
                            {new Date(event.timestamp).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: columnWidths.endpoint }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {typeof event.sourceMethod === 'string' ? event.sourceMethod : 'N/A'} {typeof event.sourceEndpoint === 'string' ? event.sourceEndpoint : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: columnWidths.status }}>
                          <Chip
                            icon={getStatusIcon(event.success)}
                            label={event.success ? 'Success' : 'Error'}
                            color={getStatusColor(event.success)}
                            size="small"
                            onClick={() => handleStatusClick(event)}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell style={{ width: columnWidths.responseTime }}>
                          <Tooltip 
                            title={
                              <Box>
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                  Timing Breakdown:
                                </Typography>
                                {event.shipiumRequestTime > 0 && (
                                  <Typography variant="caption" display="block">
                                    • Shipium API: {formatDuration(event.shipiumRequestTime)}
                                  </Typography>
                                )}
                                {event.labelaryRequestTime > 0 && (
                                  <Typography variant="caption" display="block">
                                    • Labelary: {formatDuration(event.labelaryRequestTime)}
                                  </Typography>
                                )}
                                {event.shipiumRequestTime > 0 && (
                                  <Typography variant="caption" display="block">
                                    • Other: {formatDuration(event.totalRequestTime - (event.shipiumRequestTime || 0) - (event.labelaryRequestTime || 0))}
                                  </Typography>
                                )}
                                <Divider sx={{ my: 0.5, borderColor: 'rgba(255,255,255,0.3)' }} />
                                <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                  Total: {formatDuration(event.totalRequestTime)}
                                </Typography>
                              </Box>
                            }
                            placement="right"
                            arrow
                          >
                            <Box sx={{ cursor: 'help' }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {formatDuration(event.totalRequestTime)}
                              </Typography>
                              {event.shipiumRequestTime > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Box 
                                    sx={{ 
                                      width: 8, 
                                      height: 8, 
                                      borderRadius: '50%', 
                                      bgcolor: 'primary.main' 
                                    }} 
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDuration(event.shipiumRequestTime)}
                                  </Typography>
                                  {event.labelaryRequestTime > 0 && (
                                    <>
                                      <Box 
                                        sx={{ 
                                          width: 8, 
                                          height: 8, 
                                          borderRadius: '50%', 
                                          bgcolor: 'warning.main',
                                          ml: 0.5 
                                        }} 
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDuration(event.labelaryRequestTime)}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ width: columnWidths.shipmentId }}>
                          {event.sourceEndpoint?.includes('/labels/create') && event.shipiumShipmentId ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                title={event.shipiumShipmentId}
                              >
                                {event.shipiumShipmentId}
                              </Typography>
                              <Tooltip title={copiedId === event.shipiumShipmentId ? 'Copied!' : 'Copy full ID'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyShipmentId(event.shipiumShipmentId)}
                                  sx={{ 
                                    opacity: 0.7,
                                    '&:hover': { opacity: 1 },
                                    color: copiedId === event.shipiumShipmentId ? 'success.main' : 'inherit'
                                  }}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Open in Shipium">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenShipiumReport(event.shipiumShipmentId)}
                                  sx={{ 
                                    opacity: 0.7,
                                    '&:hover': { opacity: 1 }
                                  }}
                                >
                                  <Upload fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.trackingId }}>
                          {(() => {
                            const trackingId = event.sourceResponseBody?.carrierLabel?.carrierTrackingId || 
                                              event.shipiumResponseBody?.carrierLabel?.carrierTrackingId ||
                                              event.sourceResponseBody?.trackingNumber || 
                                              event.shipiumResponseBody?.trackingNumber ||
                                              event.sourceResponseBody?.tracking?.trackingNumber ||
                                              event.shipiumResponseBody?.tracking?.trackingNumber;
                            const shipiumLabelId = event.shipiumLabelId || 
                                                   event.sourceResponseBody?.carrierLabel?.shipiumLabelId ||
                                                   event.shipiumResponseBody?.carrierLabel?.shipiumLabelId;
                            const isLabelEvent = event.sourceEndpoint?.includes('/labels/create');
                            const isCancelling = cancellingLabelId === shipiumLabelId;
                            
                            return trackingId ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    fontSize: '0.875rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                  title={trackingId}
                                >
                                  {trackingId}
                                </Typography>
                                <Tooltip title={copiedId === trackingId ? 'Copied!' : 'Copy Tracking ID'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyTrackingId(trackingId)}
                                    sx={{ 
                                      opacity: 0.7,
                                      '&:hover': { opacity: 1 },
                                      color: copiedId === trackingId ? 'success.main' : 'inherit'
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isLabelEvent && shipiumLabelId && (
                                  <Tooltip title="Cancel Label">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleCancelLabelClick(event)}
                                      disabled={isCancelling}
                                      sx={{ 
                                        opacity: 0.7,
                                        '&:hover': { opacity: 1 },
                                        color: 'error.main'
                                      }}
                                    >
                                      {isCancelling ? (
                                        <CircularProgress size={16} />
                                      ) : (
                                        <CancelIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.carrier }}>
                          {(() => {
                            const carrier = event.sourceResponseBody?.carrierLabel?.carrier || 
                                          event.shipiumResponseBody?.carrierLabel?.carrier ||
                                          event.sourceResponseBody?.carrier || 
                                          event.shipiumResponseBody?.carrier ||
                                          event.sourceResponseBody?.carrierId ||
                                          event.shipiumResponseBody?.carrierId;
                            return carrier ? (
                              <Typography variant="body2">
                                {carrier}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.files }}>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {event.labelProcessingMetrics?.filesGenerated.zpl && (
                              <Chip 
                                label={downloadingFile === `${event.shipiumShipmentId}-ZPL` ? 'Downloading...' : 'ZPL'} 
                                size="small" 
                                color="primary" 
                                clickable
                                disabled={downloadingFile === `${event.shipiumShipmentId}-ZPL`}
                                onClick={() => handleDownloadFile(event.shipiumShipmentId, 'ZPL')}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                            {event.labelProcessingMetrics?.filesGenerated.pdf && (
                              <Chip 
                                label={downloadingFile === `${event.shipiumShipmentId}-PDF` ? 'Downloading...' : 'PDF'} 
                                size="small" 
                                color="secondary" 
                                clickable
                                disabled={downloadingFile === `${event.shipiumShipmentId}-PDF`}
                                onClick={() => handleDownloadFile(event.shipiumShipmentId, 'PDF')}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                            {event.labelProcessingMetrics?.filesGenerated.png && (
                              <Chip 
                                label={downloadingFile === `${event.shipiumShipmentId}-PNG` ? 'Downloading...' : 'PNG'} 
                                size="small" 
                                color="success" 
                                clickable
                                disabled={downloadingFile === `${event.shipiumShipmentId}-PNG`}
                                onClick={() => handleDownloadFile(event.shipiumShipmentId, 'PNG')}
                                sx={{ cursor: 'pointer' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <TablePagination
                component="div"
                count={pagination?.total || 0}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Events per page:"
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              />
        </CardContent>
      </Card>
        </>
      )}

      {/* Request/Response Details Dialog */}
      <Dialog 
        open={responseDialogOpen} 
        onClose={() => setResponseDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" component="span">
                Event Details
              </Typography>
              <Chip 
                icon={getStatusIcon(selectedEvent?.success)}
                label={selectedEvent?.success ? 'Success' : 'Error'}
                color={selectedEvent?.success ? 'success' : 'error'}
                size="small"
              />
              <Chip 
                label={`${selectedEvent?.statusCode || 'N/A'}`}
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>
          <Divider />
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Event Metadata Card */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader 
              title="Request Metadata" 
              titleTypographyProps={{ variant: 'subtitle1' }}
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Timestamp
                  </Typography>
                  <Typography variant="body2">
                    {selectedEvent?.timestamp ? new Date(selectedEvent.timestamp).toLocaleString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Total Time
                  </Typography>
                  <Typography variant="body2" color="primary.main" fontWeight="medium">
                    {formatDuration(selectedEvent?.totalRequestTime || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Shipium API
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedEvent?.shipiumRequestTime ? formatDuration(selectedEvent.shipiumRequestTime) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Labelary
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedEvent?.labelaryRequestTime ? formatDuration(selectedEvent.labelaryRequestTime) : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Other Processing
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedEvent?.totalRequestTime ? 
                      formatDuration(
                        selectedEvent.totalRequestTime - 
                        (selectedEvent.shipiumRequestTime || 0) - 
                        (selectedEvent.labelaryRequestTime || 0)
                      ) : 'N/A'}
                  </Typography>
                </Grid>
                {/* Visual Timing Breakdown */}
                {selectedEvent?.shipiumRequestTime > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Timing Breakdown (%)
                    </Typography>
                    <Box sx={{ display: 'flex', width: '100%', height: 32, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      {/* Shipium API Time */}
                      <Tooltip title={`Shipium API: ${formatDuration(selectedEvent.shipiumRequestTime)} (${((selectedEvent.shipiumRequestTime / selectedEvent.totalRequestTime) * 100).toFixed(1)}%)`}>
                        <Box 
                          sx={{ 
                            width: `${(selectedEvent.shipiumRequestTime / selectedEvent.totalRequestTime) * 100}%`,
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s',
                            '&:hover': {
                              opacity: 0.8,
                            }
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>
                            {((selectedEvent.shipiumRequestTime / selectedEvent.totalRequestTime) * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      </Tooltip>
                      {/* Labelary Time */}
                      {selectedEvent.labelaryRequestTime > 0 && (
                        <Tooltip title={`Labelary: ${formatDuration(selectedEvent.labelaryRequestTime)} (${((selectedEvent.labelaryRequestTime / selectedEvent.totalRequestTime) * 100).toFixed(1)}%)`}>
                          <Box 
                            sx={{ 
                              width: `${(selectedEvent.labelaryRequestTime / selectedEvent.totalRequestTime) * 100}%`,
                              bgcolor: 'warning.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s',
                              '&:hover': {
                                opacity: 0.8,
                              }
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>
                              {((selectedEvent.labelaryRequestTime / selectedEvent.totalRequestTime) * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                      {/* Other Processing Time */}
                      {(selectedEvent.totalRequestTime - selectedEvent.shipiumRequestTime - (selectedEvent.labelaryRequestTime || 0)) > 0 && (
                        <Tooltip title={`Other Processing: ${formatDuration(selectedEvent.totalRequestTime - selectedEvent.shipiumRequestTime - (selectedEvent.labelaryRequestTime || 0))} (${(((selectedEvent.totalRequestTime - selectedEvent.shipiumRequestTime - (selectedEvent.labelaryRequestTime || 0)) / selectedEvent.totalRequestTime) * 100).toFixed(1)}%)`}>
                          <Box 
                            sx={{ 
                              width: `${((selectedEvent.totalRequestTime - selectedEvent.shipiumRequestTime - (selectedEvent.labelaryRequestTime || 0)) / selectedEvent.totalRequestTime) * 100}%`,
                              bgcolor: 'info.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.3s',
                              '&:hover': {
                                opacity: 0.8,
                              }
                            }}
                          >
                            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.7rem' }}>
                              {(((selectedEvent.totalRequestTime - selectedEvent.shipiumRequestTime - (selectedEvent.labelaryRequestTime || 0)) / selectedEvent.totalRequestTime) * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                    {/* Legend */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: 'primary.main', borderRadius: 0.5 }} />
                        <Typography variant="caption">Shipium API</Typography>
                      </Box>
                      {selectedEvent.labelaryRequestTime > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 12, height: 12, bgcolor: 'warning.main', borderRadius: 0.5 }} />
                          <Typography variant="caption">Labelary</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: 'info.main', borderRadius: 0.5 }} />
                        <Typography variant="caption">Other Processing</Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Shipment ID
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {selectedEvent?.shipiumShipmentId ? `${selectedEvent.shipiumShipmentId.substring(0, 20)}...` : 'N/A'}
                    </Typography>
                    {selectedEvent?.shipiumShipmentId && (
                      <Tooltip title={copiedId === selectedEvent.shipiumShipmentId ? 'Copied!' : 'Copy full ID'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyShipmentId(selectedEvent.shipiumShipmentId)}
                          sx={{ ml: 0.5, p: 0.5 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Endpoint
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {selectedEvent?.sourceMethod} {selectedEvent?.sourceEndpoint}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Source Request Section */}
          <Accordion defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'white',
                },
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Source Request Body
                </Typography>
                <Tooltip title={copiedText === 'sourceRequest' ? 'Copied!' : 'Copy to Clipboard'}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyToClipboard(
                        formatJsonPayload(selectedEvent?.sourceRequestBody) || 'No request body available',
                        'sourceRequest'
                      );
                    }}
                    sx={{ color: 'white', mr: 1 }}
                  >
                    {copiedText === 'sourceRequest' ? <Check /> : <ContentCopy />}
                  </IconButton>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.900', 
                  color: 'grey.100',
                  maxHeight: 400, 
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  borderRadius: 0
                }}
              >
                <pre style={{ margin: 0, color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selectedEvent?.sourceRequestBody 
                    ? formatJsonPayload(selectedEvent.sourceRequestBody)
                    : 'No request body available'
                  }
                </pre>
              </Paper>
            </AccordionDetails>
          </Accordion>

          {/* Source Response Section */}
          <Accordion defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ 
                bgcolor: selectedEvent?.success ? 'success.main' : 'error.main',
                color: 'white',
                '& .MuiAccordionSummary-expandIconWrapper': {
                  color: 'white',
                },
                '&:hover': {
                  bgcolor: selectedEvent?.success ? 'success.dark' : 'error.dark',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Source Response Body
                </Typography>
                <Tooltip title={copiedText === 'sourceResponse' ? 'Copied!' : 'Copy to Clipboard'}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyToClipboard(
                        formatJsonPayload(selectedEvent?.sourceResponseBody) || 'No response body available',
                        'sourceResponse'
                      );
                    }}
                    sx={{ color: 'white', mr: 1 }}
                  >
                    {copiedText === 'sourceResponse' ? <Check /> : <ContentCopy />}
                  </IconButton>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.900', 
                  color: 'grey.100',
                  maxHeight: 400, 
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  borderRadius: 0
                }}
              >
                <pre style={{ margin: 0, color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selectedEvent?.sourceResponseBody 
                    ? formatJsonPayload(selectedEvent.sourceResponseBody)
                    : 'No response body available'
                  }
                </pre>
              </Paper>
            </AccordionDetails>
          </Accordion>

          {/* Shipium Request Section (if available) */}
          {selectedEvent?.shipiumRequestBody && (
            <Accordion sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'info.main',
                  color: 'white',
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    color: 'white',
                  },
                  '&:hover': {
                    bgcolor: 'info.dark',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Shipium Request Body
                  </Typography>
                  <Tooltip title={copiedText === 'shipiumRequest' ? 'Copied!' : 'Copy to Clipboard'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyToClipboard(
                          formatJsonPayload(selectedEvent?.shipiumRequestBody) || 'No request body available',
                          'shipiumRequest'
                        );
                      }}
                      sx={{ color: 'white', mr: 1 }}
                    >
                      {copiedText === 'shipiumRequest' ? <Check /> : <ContentCopy />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'grey.900', 
                    color: 'grey.100',
                    maxHeight: 400, 
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    borderRadius: 0
                  }}
                >
                  <pre style={{ margin: 0, color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selectedEvent?.shipiumRequestBody 
                      ? formatJsonPayload(selectedEvent.shipiumRequestBody)
                      : 'No request body available'
                    }
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Shipium Response Section (if available) */}
          {selectedEvent?.shipiumResponseBody && (
            <Accordion sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'secondary.main',
                  color: 'white',
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    color: 'white',
                  },
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    Shipium Response Body
                  </Typography>
                  <Tooltip title={copiedText === 'shipiumResponse' ? 'Copied!' : 'Copy to Clipboard'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyToClipboard(
                          formatJsonPayload(selectedEvent?.shipiumResponseBody) || 'No response body available',
                          'shipiumResponse'
                        );
                      }}
                      sx={{ color: 'white', mr: 1 }}
                    >
                      {copiedText === 'shipiumResponse' ? <Check /> : <ContentCopy />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'grey.900', 
                    color: 'grey.100',
                    maxHeight: 400, 
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    borderRadius: 0
                  }}
                >
                  <pre style={{ margin: 0, color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selectedEvent?.shipiumResponseBody 
                      ? formatJsonPayload(selectedEvent.shipiumResponseBody)
                      : 'No response body available'
                    }
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Error Message (if any) */}
          {selectedEvent?.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <strong>Error:</strong> {selectedEvent.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Options Dialog */}
      <Dialog 
        open={exportDialogOpen} 
        onClose={() => setExportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Export Analytics Data
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Choose export format:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box 
                sx={{ 
                  p: 2, 
                  border: exportType === 'summary' ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: exportType === 'summary' ? '#e3f2fd' : 'background.paper',
                  '&:hover': {
                    backgroundColor: exportType === 'summary' ? '#e3f2fd' : 'action.hover'
                  }
                }}
                onClick={() => setExportType('summary')}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={exportType === 'summary' ? 'primary.main' : 'text.primary'}>
                  Summary (CSV)
                </Typography>
                <Typography variant="body2" color={exportType === 'summary' ? 'primary.dark' : 'text.secondary'}>
                  Export summary metrics, endpoint breakdown, and performance data as CSV
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  border: exportType === 'summary-json' ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: exportType === 'summary-json' ? '#e3f2fd' : 'background.paper',
                  '&:hover': {
                    backgroundColor: exportType === 'summary-json' ? '#e3f2fd' : 'action.hover'
                  }
                }}
                onClick={() => setExportType('summary-json')}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={exportType === 'summary-json' ? 'primary.main' : 'text.primary'}>
                  Summary (JSON)
                </Typography>
                <Typography variant="body2" color={exportType === 'summary-json' ? 'primary.dark' : 'text.secondary'}>
                  Export summary metrics, endpoint breakdown, and performance data as JSON
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  border: exportType === 'details' ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: exportType === 'details' ? '#e3f2fd' : 'background.paper',
                  '&:hover': {
                    backgroundColor: exportType === 'details' ? '#e3f2fd' : 'action.hover'
                  }
                }}
                onClick={() => setExportType('details')}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={exportType === 'details' ? 'primary.main' : 'text.primary'}>
                  Details (CSV)
                </Typography>
                <Typography variant="body2" color={exportType === 'details' ? 'primary.dark' : 'text.secondary'}>
                  Export all filtered events with detailed information as CSV
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  border: exportType === 'details-json' ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: exportType === 'details-json' ? '#e3f2fd' : 'background.paper',
                  '&:hover': {
                    backgroundColor: exportType === 'details-json' ? '#e3f2fd' : 'action.hover'
                  }
                }}
                onClick={() => setExportType('details-json')}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={exportType === 'details-json' ? 'primary.main' : 'text.primary'}>
                  Details (JSON)
                </Typography>
                <Typography variant="body2" color={exportType === 'details-json' ? 'primary.dark' : 'text.secondary'}>
                  Export all filtered events with detailed information as JSON
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  border: exportType === 'pdf' ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: exportType === 'pdf' ? '#e3f2fd' : 'background.paper',
                  '&:hover': {
                    backgroundColor: exportType === 'pdf' ? '#e3f2fd' : 'action.hover'
                  }
                }}
                onClick={() => setExportType('pdf')}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={exportType === 'pdf' ? 'primary.main' : 'text.primary'}>
                  PDF Report
                </Typography>
                <Typography variant="body2" color={exportType === 'pdf' ? 'primary.dark' : 'text.secondary'}>
                  Generate a formatted HTML report that can be printed to PDF
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  p: 2, 
                  border: exportType === 'png' ? '2px solid #1976d2' : '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: exportType === 'png' ? '#e3f2fd' : 'background.paper',
                  '&:hover': {
                    backgroundColor: exportType === 'png' ? '#e3f2fd' : 'action.hover'
                  }
                }}
                onClick={() => setExportType('png')}
              >
                <Typography variant="subtitle1" fontWeight="bold" color={exportType === 'png' ? 'primary.main' : 'text.primary'}>
                  PNG Report
                </Typography>
                <Typography variant="body2" color={exportType === 'png' ? 'primary.dark' : 'text.secondary'}>
                  Generate a formatted HTML report that can be captured as PNG
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Label Confirmation Dialog */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleCancelDismiss}
        aria-labelledby="cancel-label-dialog-title"
        aria-describedby="cancel-label-dialog-description"
      >
        <DialogTitle id="cancel-label-dialog-title" sx={{ color: 'error.main' }}>
          Confirm Label Cancellation
        </DialogTitle>
        <DialogContent>
          <Typography id="cancel-label-dialog-description" variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to cancel this label?
          </Typography>
          {labelToCancel && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                <strong>Label ID:</strong> {labelToCancel.shipiumLabelId}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The label will be cancelled and voided.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDismiss} color="inherit">
            No, Keep Label
          </Button>
          <Button 
            onClick={handleCancelConfirm} 
            variant="contained" 
            color="error"
            autoFocus
          >
            Yes, Cancel Label
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Analytics;